import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faPencil,
  faPlus,
  faSpinner,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
  inspectionParameterDefaultsService,
  inspectionParameterService,
  type DefaultInspectionParameter,
  type InspectionParameter,
  type InspectionParameterOverviewItem,
} from '@/services/inspection-parameter.service';
import { PageHeader } from '@/components/shared/PageHeader';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useHasPermission } from '@/hooks/use-permissions';

/**
 * Master Data → Inspection Parameters (Group 4 + default-template enhancement).
 *
 * Three levels, and the page exists to make the difference between them obvious,
 * because an administrator reordering a list has no other way to know which of
 * the three they are about to change:
 *
 *   DEFAULT TEMPLATE  — the starting list. Affects models configured FROM NOW ON.
 *   MODEL CONFIG      — independent per model. Affects inspections created FROM NOW ON.
 *   INSPECTION        — a permanent snapshot. Never changes retroactively.
 *
 * The copy from default → model is made HERE, by value, and is committed only by
 * "Save Configuration". Opening Configure writes nothing, which is why a later
 * template edit can never reach a model that was already configured.
 *
 * "Others" is a system constant the backend appends at inspection creation. It is
 * shown as an informational footnote on the default card and as a locked final
 * row in the model editor, and is never part of the default template.
 */

/** Mirrors the backend constant. Display only — the server appends the real row. */
const OTHERS_LABEL = 'Others';

/** Matches the backend `label` column and its DTO bound. */
const LABEL_MAX_LENGTH = 255;

interface ParameterDialogProps {
  title: string;
  initialValue: string;
  existing: string[];
  onSubmit: (label: string) => void;
  onCancel: () => void;
}

function ParameterDialog({
  title,
  initialValue,
  existing,
  onSubmit,
  onCancel,
}: ParameterDialogProps) {
  const [label, setLabel] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = label.trim();
    if (trimmed.length === 0) {
      setError('A parameter name is required');
      return;
    }
    if (trimmed.length > LABEL_MAX_LENGTH) {
      setError(`Keep the name to ${LABEL_MAX_LENGTH} characters or fewer`);
      return;
    }
    // Mirrors the server guard so the operator is told before a round trip.
    if (trimmed.toLowerCase() === OTHERS_LABEL.toLowerCase()) {
      setError(`"${OTHERS_LABEL}" is added automatically and cannot be configured`);
      return;
    }
    if (existing.some((l) => l.toLowerCase() === trimmed.toLowerCase())) {
      setError('That parameter is already in the list');
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField
        label="Parameter name"
        htmlFor="parameter-label"
        required
        error={error ?? undefined}
      >
        <Input
          id="parameter-label"
          value={label}
          maxLength={LABEL_MAX_LENGTH}
          autoFocus
          onChange={(e) => {
            setLabel(e.target.value);
            setError(null);
          }}
        />
      </FormField>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">{title === 'Add Inspection Parameter' ? 'Add' : 'Save'}</Button>
      </div>
    </form>
  );
}

interface ParameterEditorTableProps {
  labels: string[];
  canEdit: boolean;
  /** Renders the locked "Others" row after the list. Off for the default template. */
  showOthers: boolean;
  onMove: (index: number, delta: -1 | 1) => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  emptyMessage: string;
}

/**
 * The ordered editor table, shared by the default template and the per-model
 * configuration so the two cannot drift apart in behaviour.
 *
 * The "Others" row, when shown, carries no control at all — not a disabled one.
 * There is therefore no path in the UI that could turn it into an ordinary
 * parameter, which is the same guarantee the backend gets from it having no
 * master row.
 */
function ParameterEditorTable({
  labels,
  canEdit,
  showOthers,
  onMove,
  onEdit,
  onDelete,
  emptyMessage,
}: ParameterEditorTableProps) {
  return (
    <>
      {labels.length === 0 && (
        <p className="px-4 py-6 text-sm text-muted-foreground">{emptyMessage}</p>
      )}

      {labels.length > 0 && (
        <table className="w-full text-sm">
          <tbody>
            {labels.map((label, i) => (
              <tr key={`${label}-${i}`} className="border-b border-border last:border-0">
                <td className="w-12 px-4 py-2 text-muted-foreground">{i + 1}</td>
                <td className="px-4 py-2 text-foreground">{label}</td>
                <td className="w-40 px-4 py-2 text-right">
                  {canEdit && (
                    <span className="inline-flex gap-3">
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                        aria-label={`Move ${label} up`}
                        disabled={i === 0}
                        onClick={() => onMove(i, -1)}
                      >
                        <FontAwesomeIcon icon={faArrowUp} />
                      </button>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                        aria-label={`Move ${label} down`}
                        disabled={i === labels.length - 1}
                        onClick={() => onMove(i, 1)}
                      >
                        <FontAwesomeIcon icon={faArrowDown} />
                      </button>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={`Edit ${label}`}
                        onClick={() => onEdit(i)}
                      >
                        <FontAwesomeIcon icon={faPencil} />
                      </button>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-destructive"
                        aria-label={`Delete ${label}`}
                        onClick={() => onDelete(i)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}

            {showOthers && (
              <tr className="border-t border-border bg-muted/40">
                <td className="w-12 px-4 py-2 text-muted-foreground">{labels.length + 1}</td>
                <td className="px-4 py-2 text-foreground">{OTHERS_LABEL}</td>
                <td className="w-40 px-4 py-2 text-right text-xs uppercase tracking-wide text-muted-foreground">
                  Fixed / Final / Locked
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

type EditorMode =
  | { kind: 'none' }
  | { kind: 'default' }
  | {
      kind: 'model';
      modelId: string;
      modelLabel: string;
      /** True when the editor was seeded from the default template. */
      seeded: boolean;
    };

export function InspectionParametersPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const canEdit = can('model:update');

  const [editor, setEditor] = useState<EditorMode>({ kind: 'none' });
  /** Working copy. Only committed to the server by an explicit Save. */
  const [labels, setLabels] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const defaultsQuery = useQuery({
    queryKey: ['inspection-parameter-defaults'],
    queryFn: () => inspectionParameterDefaultsService.list(),
  });

  const overviewQuery = useQuery({
    queryKey: ['inspection-parameter-overview'],
    queryFn: () => inspectionParameterService.overview(),
  });

  const modelId = editor.kind === 'model' ? editor.modelId : null;
  const modelConfigQuery = useQuery({
    queryKey: ['inspection-parameters', modelId],
    queryFn: () => inspectionParameterService.list(modelId!),
    enabled: !!modelId,
  });

  /**
   * Seeds the working copy once the editor's own data arrives.
   *
   * A model with no configuration starts from a COPY of the current default
   * template — taken here, in the browser, and not persisted until Save. An
   * already-configured model starts from its own rows and the default is not
   * consulted at all.
   */
  useEffect(() => {
    if (editor.kind === 'default') {
      setLabels((defaultsQuery.data ?? []).map((d: DefaultInspectionParameter) => d.label));
      setDirty(false);
      return;
    }
    if (editor.kind === 'model' && modelConfigQuery.data) {
      const own = modelConfigQuery.data.map((p: InspectionParameter) => p.label);
      if (own.length > 0) {
        setLabels(own);
      } else {
        setLabels((defaultsQuery.data ?? []).map((d) => d.label));
      }
      setDirty(false);
    }
  }, [editor, defaultsQuery.data, modelConfigQuery.data]);

  const saveDefaults = useMutation({
    mutationFn: () => inspectionParameterDefaultsService.save(labels),
    onSuccess: () => {
      toast.success('Default inspection parameters saved');
      setDirty(false);
      setEditor({ kind: 'none' });
      void queryClient.invalidateQueries({ queryKey: ['inspection-parameter-defaults'] });
    },
    onError: () => toast.error('Could not save the default template'),
  });

  const saveModel = useMutation({
    mutationFn: () => inspectionParameterService.save(modelId!, labels),
    onSuccess: () => {
      toast.success('Inspection parameters saved');
      setDirty(false);
      setEditor({ kind: 'none' });
      void queryClient.invalidateQueries({ queryKey: ['inspection-parameters', modelId] });
      void queryClient.invalidateQueries({ queryKey: ['inspection-parameter-overview'] });
    },
    onError: () => toast.error('Could not save the configuration'),
  });

  const handleMove = (index: number, delta: -1 | 1) => {
    setLabels((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
    setDirty(true);
  };

  const handleAdd = (label: string) => {
    // Appended to the end of the configured list — which puts it immediately
    // BEFORE the fixed "Others" row, since Others is not part of this array.
    setLabels((prev) => [...prev, label]);
    setDirty(true);
    setAdding(false);
  };

  const handleEditSubmit = (label: string) => {
    if (editingIndex === null) return;
    setLabels((prev) => prev.map((l, i) => (i === editingIndex ? label : l)));
    setDirty(true);
    setEditingIndex(null);
  };

  const handleDelete = async (index: number) => {
    const confirmed = await confirmDelete(`Remove "${labels[index]}" from this list?`);
    if (!confirmed) return;
    setLabels((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const closeEditor = () => {
    // Cancel discards the working copy and writes nothing.
    setEditor({ kind: 'none' });
    setLabels([]);
    setDirty(false);
  };

  const defaults = defaultsQuery.data ?? [];

  // The `?? []` lives INSIDE the memo: a fresh literal in the dependency array
  // would change identity on every render and defeat the memo entirely.
  const filteredOverview = useMemo(() => {
    const rows = overviewQuery.data ?? [];
    const q = search.trim().toLowerCase();
    if (q.length === 0) return rows;
    return rows.filter((m: InspectionParameterOverviewItem) =>
      `${m.modelNumber ?? ''} ${m.name ?? ''}`.toLowerCase().includes(q),
    );
  }, [overviewQuery.data, search]);

  const modelDisplay = (m: InspectionParameterOverviewItem) =>
    m.modelNumber ?? m.name ?? m.equipmentModelId;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inspection Parameters"
        description="Set the default As-Received / As-Found checks, then configure each equipment model. Changes apply to inspections created from now on; inspections that already exist keep the checklist they were created with."
      />

      {/* ── DEFAULT TEMPLATE ───────────────────────────────────────────────── */}
      <section
        className="rounded-lg border border-border bg-card"
        aria-label="Default inspection parameters"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">DEFAULT INSPECTION PARAMETERS</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              These parameters are used as the starting list when configuring a model for the first
              time. Changes here do <strong>not</strong> affect existing models or inspections.
            </p>
          </div>
          {canEdit && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditor({ kind: 'default' })}
            >
              Edit Default
            </Button>
          )}
        </div>

        {defaultsQuery.isLoading ? (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <FontAwesomeIcon icon={faSpinner} spin /> Loading default template…
          </div>
        ) : (
          <>
            {defaults.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground">
                No default parameters configured yet. A model configured now starts from an empty
                list.
              </p>
            ) : (
              <ol className="px-4 py-3 text-sm text-foreground">
                {defaults.map((d, i) => (
                  <li key={d.id} className="py-1">
                    <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                    {d.label}
                  </li>
                ))}
              </ol>
            )}
            <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
              + {OTHERS_LABEL} (automatically added to inspections)
            </p>
          </>
        )}
      </section>

      {/* ── MODEL OVERVIEW ─────────────────────────────────────────────────── */}
      <section
        className="rounded-lg border border-border bg-card"
        aria-label="Configured equipment models"
      >
        <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">Configured Equipment Models</h2>
          <div className="w-64">
            <Input
              aria-label="Search model"
              placeholder="Search model…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {overviewQuery.isLoading ? (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <FontAwesomeIcon icon={faSpinner} spin /> Loading models…
          </div>
        ) : filteredOverview.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">No equipment models found.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2 font-medium">Equipment Model</th>
                <th className="px-4 py-2 font-medium">Parameters</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredOverview.map((m) => (
                <tr key={m.equipmentModelId} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 text-foreground">{modelDisplay(m)}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {m.isConfigured ? `${m.parameterCount} + ${OTHERS_LABEL}` : '—'}
                  </td>
                  <td className="px-4 py-2">
                    {m.isConfigured ? (
                      <span className="text-foreground">Configured</span>
                    ) : (
                      <span className="text-muted-foreground">Not Configured</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canEdit && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          setEditor({
                            kind: 'model',
                            modelId: m.equipmentModelId,
                            modelLabel: modelDisplay(m),
                            seeded: !m.isConfigured,
                          })
                        }
                      >
                        {m.isConfigured ? 'Edit' : 'Configure'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── DEFAULT EDITOR ─────────────────────────────────────────────────── */}
      {editor.kind === 'default' && (
        <Modal open title="Default Inspection Parameters" onClose={closeEditor}>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              These are the starting parameters for newly configured models. Changing this list does{' '}
              <strong>not</strong> change existing model configurations.
            </p>

            <div className="rounded-lg border border-border">
              <ParameterEditorTable
                labels={labels}
                canEdit={canEdit}
                showOthers={false}
                onMove={handleMove}
                onEdit={setEditingIndex}
                onDelete={(i) => void handleDelete(i)}
                emptyMessage="No default parameters yet."
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <Button type="button" variant="secondary" onClick={() => setAdding(true)}>
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Add Parameter
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" onClick={closeEditor}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={!dirty || saveDefaults.isPending}
                  onClick={() => saveDefaults.mutate()}
                >
                  {saveDefaults.isPending && (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  )}
                  Save Default
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODEL EDITOR ───────────────────────────────────────────────────── */}
      {editor.kind === 'model' && (
        <Modal open title="Configure Inspection Parameters" onClose={closeEditor}>
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Equipment Model
              </p>
              <p className="text-sm font-medium text-foreground">{editor.modelLabel}</p>
            </div>

            {editor.seeded && (
              <p className="text-sm text-muted-foreground">
                Starting from Default Configuration. Nothing is saved until you choose{' '}
                <strong>Save Configuration</strong>.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Applies to inspections created from now on. Existing inspections keep the checklist
              they were created with.
            </p>

            {modelConfigQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FontAwesomeIcon icon={faSpinner} spin /> Loading configuration…
              </div>
            ) : (
              <div className="rounded-lg border border-border">
                <ParameterEditorTable
                  labels={labels}
                  canEdit={canEdit}
                  showOthers
                  onMove={handleMove}
                  onEdit={setEditingIndex}
                  onDelete={(i) => void handleDelete(i)}
                  emptyMessage="No inspection parameters configured for this model."
                />
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              <Button type="button" variant="secondary" onClick={() => setAdding(true)}>
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Add More
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" onClick={closeEditor}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={!dirty || saveModel.isPending}
                  onClick={() => saveModel.mutate()}
                >
                  {saveModel.isPending && (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  )}
                  Save Configuration
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {adding && (
        <Modal open title="Add Inspection Parameter" onClose={() => setAdding(false)}>
          <ParameterDialog
            title="Add Inspection Parameter"
            initialValue=""
            existing={labels}
            onSubmit={handleAdd}
            onCancel={() => setAdding(false)}
          />
        </Modal>
      )}

      {editingIndex !== null && (
        <Modal open title="Edit Inspection Parameter" onClose={() => setEditingIndex(null)}>
          <ParameterDialog
            title="Edit Inspection Parameter"
            initialValue={labels[editingIndex] ?? ''}
            existing={labels.filter((_, i) => i !== editingIndex)}
            onSubmit={handleEditSubmit}
            onCancel={() => setEditingIndex(null)}
          />
        </Modal>
      )}
    </div>
  );
}
